import classNames from 'classnames/bind';
import styles from './TableBody.module.css';
import { isMobile, isSmallScreenMediaQuery } from '../../../utils/util';
import { useIntersect } from '../../../hooks/useIntersect';
import { RefObject, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import {
  quizListPageChanged,
  selectQuiz,
} from '../../../features/analysisSlice';
import { formulaManager } from '../../../utils/formulaManager';

interface TableBody {
  name: string;
  list: any[];
  keyList: string[];
  selectOptions?: {
    idx: number;
    default: { key: string; value: string };
    values: string[];
    onClick: (value: string, i: number) => void;
  };
  onClick: (id: number) => void;
}
export default function TableBody({
  name,
  list,
  keyList,
  selectOptions,
  onClick,
}: TableBody) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const quiz = useAppSelector(selectQuiz);
  const tableBodyRef = useRef(null);
  const cellRef = useRef<(HTMLSpanElement | null)[]>([]);

  const intersectRef = useIntersect(
    async (entry, observer) => {
      if (entry.target instanceof HTMLElement) {
        if (name === 'quiz-list') {
          const idx = Number(entry.target.dataset.idx);
          const page = Math.floor(idx / 30) + (idx && idx % 29 === 0 ? 1 : 0);
          dispatch(quizListPageChanged(page));
        }
      }
      observer.unobserve(entry.target);
    },
    undefined,
    tableBodyRef
  );

  const clickItem = (data: any) => {
    onClick(data);
  };

  const convertLatex = (value: string, i: number) => {
    if (cellRef.current[i]) {
      formulaManager.setFormulaText(cellRef.current[i], value);
    }
  };

  return (
    <div className={cx('table_body')} ref={tableBodyRef}>
      {list.map((data, i) => (
        <div
          key={data.id ?? data.seq}
          ref={intersectRef as RefObject<HTMLDivElement>}
          className={cx('items', {
            on: data.seq === quiz.seq,
          })}
          onClick={() => clickItem(data)}
          data-idx={i}
        >
          {keyList.map((key, i) => {
            const hasLatex =
              (key === 'answer' || key === 'userAnswer') && data.formula;

            return (
              <div key={key} className={cx('sub')}>
                <span
                  className={cx(key)}
                  ref={(el) => {
                    cellRef.current[i] = el;
                    hasLatex &&
                      convertLatex(data[key].replaceAll('/', ', '), i);
                  }}
                  {...(hasLatex && {
                    'data-latex': data[key].replaceAll('/', ', '),
                  })}
                >
                  {!!hasLatex ||
                    (isMobile && i === selectOptions?.idx ? (
                      data[selectOptions?.default.key]
                    ) : key === 'userAnswer' || key === 'answer' ? (
                      data[key]?.replaceAll('/', ', ')
                    ) : key === 'dispGrade' &&
                      (data[key] === 'O' || data[key] === 'X') ? (
                      <span
                        className={cx('svg_i', {
                          icon_correct: data[key] === 'O',
                          icon_incorrect: data[key] === 'X',
                        })}
                      >
                        <span className={cx('blind')}>{data[key]}</span>
                      </span>
                    ) : (
                      data[key]
                    ))}
                </span>
                {key === 'rank' &&
                  (data[key] === 0 ? (
                    <span>-</span>
                  ) : isSmallScreenMediaQuery ? (
                    <span>{data[key]}</span>
                  ) : (
                    Array.from({ length: data[key] }).map((val, i) => (
                      <span
                        key={i}
                        className={cx('svg_i', 'icon_level')}
                      ></span>
                    ))
                  ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
